/* ============================================================
   Сетка турнира MLBB.

   — генерируется автоматически из зарегистрированных команд;
   — путь своей команды подсвечен фиолетовым (будущие матчи —
     пунктиром), кнопка «Моя команда» скроллит к текущему матчу;
   — чек-ин: когда обе команды известны, открываются слоты
     готовности; как только 5 игроков с каждой стороны нажали
     «Готов» — командам открываются игровые ID соперников;
   — счёт матчей проставляет админ (демо-контролы внизу карточки
     матча), победитель автоматически проходит дальше.
   ============================================================ */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useAppStore } from '../../store';
import { useFedStore } from '../store';
import { Ic } from '../icons';
import { Rv, Pill } from '../ui';
import { useMlbb, buildRounds, regTeamIdsOf, ROUND_TITLES, MLBB_ACTIVE_TOUR_ID } from './store';
import { VIO } from './MlbbTournament';

// База для ссылок на загруженные скриншоты (uploads/ отдаёт бэкенд без /api)
const FILE_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5252/api').replace(/\/api$/, '');

const fmtClock = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

// Копирование в буфер с фолбэком для незащищённого контекста (http/IP),
// где navigator.clipboard недоступен — важно, чтобы копирование ID работало
// всегда, а не только на https.
async function copyToClipboard(text) {
  const t = String(text ?? '');
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(t);
      return true;
    }
  } catch (e) { /* фолбэк ниже */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = t;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (e) {
    return false;
  }
}

const BD = {
  ru: {
    my_team: 'Моя команда', legend_path: 'путь вашей команды', legend_future: 'возможный путь',
    st_wait: 'ожидание команд', st_checkin: 'чек-ин открыт', st_ready: 'ID открыты', st_done: 'завершён', st_bye: 'проход без игры',
    bye: 'BYE', tbd: 'Определяется…',
    bye_t: 'Проход без игры', bye_d: 'В этом раунде у команды нет соперника — она автоматически проходит в следующий раунд.', close: 'Закрыть',
    empty_t: 'Сетка появится после регистрации команд', empty_cta: 'Зарегистрировать команду',
    sel_hint: 'Нажми на матч, чтобы открыть чек-ин и составы',
    checkin_t: 'Чек-ин матча', checkin_d: 'Когда все 5 игроков обеих команд нажмут «Готов», командам откроются игровые ID соперников.',
    checkin_locked: 'Слоты откроются, когда определятся обе команды.',
    im_ready: 'Готов', im_unready: 'Отменить готовность',
    demo_ready_all: 'Отметить всю команду',
    opp_preparing: 'Соперники готовятся…',
    ids_t: 'Игровые ID соперников', ids_d: 'Добавь их в игре и создай кастомное лобби.',
    copy_ids: 'Скопировать все ID', copied: 'Скопировано',
    id_copy_hint: 'Нажми на ID — он скопируется в буфер', id_copied: 'ID скопирован', copy_fail: 'Не удалось скопировать', copy_tap: 'копировать',
    score_t: 'Счёт (админ)', score_save: 'Сохранить счёт',
    winner: 'Победа',
    reset_demo: 'Сбросить турнир',
    watch_only: 'Ты не участвуешь в этом матче — составы видны без ID.',
    ready_of: 'готовы',
    st_seed: 'соперник по сетке',
    st_await_start: 'ожидание старта',
    st_walkover: 'тех. победа',
    st_dff: 'двойное тех.',
    st_pending: 'на проверке',
    st_resolving: 'подводим итог…',
    st_playing: 'идёт матч',
    closed_t: 'Матчи ещё не начались',
    closed_d: 'Сейчас идёт регистрация. Когда она закроется, здесь откроются чек-ин и составы — а пока можно посмотреть, с кем сведёт сетка.',
    preview_hint: 'Идёт регистрация — сетка предварительная и может измениться, пока не закроется приём заявок.',
    // окно чек-ина / старт матча
    open_win: 'Открыть чек-ин (20 мин)',
    reopen_win: 'Перезапустить окно',
    await_start_admin: 'Обе команды определены. Открой чек-ин — командам даётся 20 минут отметиться «Готов».',
    await_start_user: 'Ожидание старта — админ вот-вот откроет чек-ин матча.',
    need_matches_open: 'Матчи турнира ещё не открыты для игроков. Пока не включён старт матчей, команды не увидят чек-ин — открывать окно рано.',
    win_left: 'до конца чек-ина',
    win_over: 'Окно чек-ина закрыто — подводим итог по явке.',
    win_hint: 'Успейте отметить «Готов» всей основой (5) за 20 минут. Кто не успел — техническое поражение.',
    // исходы
    out_walkover: 'Техническая победа',
    out_walkover_d: 'Соперник не явился в окно чек-ина.',
    out_dff_t: 'Двойное техническое поражение',
    out_dff_d: 'Обе команды не отметились за 20 минут — дальше по сетке не проходит никто.',
    out_confirmed: 'Результат подтверждён',
    // результат (админ)
    res_t: 'Результат матча (админ)',
    res_win_a: 'Победа',
    res_dff: 'Двойное тех.',
    res_reset: 'Сбросить результат',
    res_hint: 'Победитель сразу проходит дальше по сетке. Счёт ниже — для истории.',
    // скриншот-подтверждение
    proof_t: 'Скриншот результата',
    proof_hint: 'Победившая команда прикрепляет скрин финального экрана — админ подтвердит по нему.',
    proof_upload: 'Загрузить скриншот',
    proof_uploading: 'Загрузка…',
    proof_ok: 'Скриншот загружен — ждёт подтверждения админа',
    proof_view: 'Открыть скриншот',
    proof_pending: 'Скриншот на проверке у админа',
    proof_err: 'Не удалось загрузить файл',
  },
  kz: {
    my_team: 'Менің командам', legend_path: 'командаңның жолы', legend_future: 'ықтимал жол',
    st_wait: 'командаларды күту', st_checkin: 'чек-ин ашық', st_ready: 'ID ашылды', st_done: 'аяқталды', st_bye: 'ойынсыз өту',
    bye: 'BYE', tbd: 'Анықталуда…',
    bye_t: 'Ойынсыз өту', bye_d: 'Бұл раундта команданың қарсыласы жоқ — ол келесі кезеңге автоматты түрде өтеді.', close: 'Жабу',
    empty_t: 'Тор командалар тіркелген соң пайда болады', empty_cta: 'Команданы тіркеу',
    sel_hint: 'Чек-ин мен құрамдарды ашу үшін матчты бас',
    checkin_t: 'Матч чек-ині', checkin_d: 'Екі команданың 5 ойыншысы да «Дайынмын» басқанда, командаларға қарсыластардың ID-лері ашылады.',
    checkin_locked: 'Слоттар екі команда анықталғанда ашылады.',
    im_ready: 'Дайынмын', im_unready: 'Дайындықты болдырмау',
    demo_ready_all: 'Бүкіл команданы белгілеу',
    opp_preparing: 'Қарсыластар дайындалуда…',
    ids_t: 'Қарсыластардың ойын ID-лері', ids_d: 'Оларды ойында қосып, кастом лобби құр.',
    copy_ids: 'Барлық ID көшіру', copied: 'Көшірілді',
    id_copy_hint: 'ID-ді бассаң — буферге көшіріледі', id_copied: 'ID көшірілді', copy_fail: 'Көшіру мүмкін болмады', copy_tap: 'көшіру',
    score_t: 'Есеп (әкімші)', score_save: 'Есепті сақтау',
    winner: 'Жеңіс',
    reset_demo: 'Турнирді тазарту',
    watch_only: 'Сен бұл матчқа қатыспайсың — құрамдар ID-сіз көрінеді.',
    ready_of: 'дайын',
    st_seed: 'тордағы қарсылас',
    st_await_start: 'старт күтілуде',
    st_walkover: 'тех. жеңіс',
    st_dff: 'қос тех.',
    st_pending: 'тексеруде',
    st_resolving: 'қорытынды…',
    st_playing: 'ойын жүріп жатыр',
    closed_t: 'Матчтар әлі басталған жоқ',
    closed_d: 'Қазір тіркелу жүріп жатыр. Ол жабылған соң мұнда чек-ин мен құрамдар ашылады — әзірге тор кіммен кездестіретінін көруге болады.',
    preview_hint: 'Тіркелу жүріп жатыр — өтінімдер қабылдау жабылғанша тор алдын ала және өзгеруі мүмкін.',
    open_win: 'Чек-инді ашу (20 мин)',
    reopen_win: 'Терезені қайта ашу',
    await_start_admin: 'Екі команда да белгілі. Чек-инді аш — командаларға «Дайынмын» белгілеуге 20 минут беріледі.',
    await_start_user: 'Старт күтілуде — әкімші матч чек-инін ашады.',
    need_matches_open: 'Турнир матчтары ойыншыларға әлі ашылмаған. Матчтар старты қосылмайынша командалар чек-инді көрмейді — терезені ашу ерте.',
    win_left: 'чек-ин аяқталуына',
    win_over: 'Чек-ин терезесі жабылды — келу бойынша қорытынды шығарылуда.',
    win_hint: '20 минут ішінде негізгі құраммен (5) «Дайынмын» белгілеп үлгеріңіз. Үлгермеген — техникалық жеңіліс.',
    out_walkover: 'Техникалық жеңіс',
    out_walkover_d: 'Қарсылас чек-ин терезесінде келмеді.',
    out_dff_t: 'Қос техникалық жеңіліс',
    out_dff_d: 'Екі команда да 20 минутта белгіленбеді — торда ешкім өтпейді.',
    out_confirmed: 'Нәтиже расталды',
    res_t: 'Матч нәтижесі (әкімші)',
    res_win_a: 'Жеңіс',
    res_dff: 'Қос тех.',
    res_reset: 'Нәтижені тазарту',
    res_hint: 'Жеңімпаз бірден торда әрі қарай өтеді. Төмендегі есеп — тарих үшін.',
    proof_t: 'Нәтиже скриншоты',
    proof_hint: 'Жеңген команда финалдық экранның скринін тіркейді — әкімші сол бойынша растайды.',
    proof_upload: 'Скриншот жүктеу',
    proof_uploading: 'Жүктелуде…',
    proof_ok: 'Скриншот жүктелді — әкімшінің растауын күтуде',
    proof_view: 'Скриншотты ашу',
    proof_pending: 'Скриншот әкімшіде тексеруде',
    proof_err: 'Файлды жүктеу мүмкін болмады',
  },
};

const requiredOf = (team) => Math.min(5, team ? team.members.length : 5);

/** Строка команды внутри карточки матча */
function SideRow({ team, score, win, isMine, ready, req, showCheckin, B }) {
  const color = win ? 'var(--fed-gold)' : isMine ? VIO.bright : 'var(--fed-fg-soft)';
  return (
    <div className="flex items-center gap-2.5" style={{ padding: '9px 12px', opacity: team ? 1 : 0.45 }}>
      {team ? (
        <span className="flex-shrink-0 flex items-center justify-center font-display font-bold text-white rounded-[6px]" style={{ minWidth: 34, height: 24, padding: '0 5px', fontSize: 10, background: isMine ? `linear-gradient(140deg,${VIO.accent},${VIO.deep})` : 'var(--fed-surface-3)', border: isMine ? 'none' : '1px solid var(--fed-line)' }}>
          {team.tag}
        </span>
      ) : (
        <span className="flex-shrink-0" style={{ width: 34 }} />
      )}
      <span className="min-w-0 flex-1">
        <span className="block font-semibold truncate" style={{ fontSize: 13.5, color }}>{team ? team.name : B.tbd}</span>
        {showCheckin && team && (
          <span className="block font-mono" style={{ fontSize: 9.5, marginTop: 1, color: ready >= req ? 'var(--fed-ok)' : 'var(--fed-fg-faint)' }}>
            {ready}/{req} {B.ready_of}
          </span>
        )}
      </span>
      <span className="font-display font-bold" style={{ fontSize: 16, color }}>{score}</span>
    </div>
  );
}

/** Состав команды только для просмотра (ники + капитан), без игровых ID и
    контролов готовности. Показывается в панели матча сразу по клику — чтобы
    увидеть ростер прямо в сетке, не уходя во вкладку «Команды». Игровые ID не
    раскрываем: это делает блок обмена при полной готовности обеих команд. */
function RosterMini({ team, userKey, M }) {
  if (!team) return null;
  return (
    <div className="rounded-xl border border-line overflow-hidden" style={{ background: 'rgba(10,10,12,.45)' }}>
      <div className="flex items-center gap-2 border-b border-line" style={{ padding: '9px 13px' }}>
        <span className="flex items-center justify-center font-display font-bold text-white rounded-[7px]" style={{ minWidth: 30, height: 22, padding: '0 6px', fontSize: 10, background: `linear-gradient(140deg,${VIO.accent},${VIO.deep})` }}>{team.tag}</span>
        <span className="font-display font-bold text-fg truncate" style={{ fontSize: 13.5 }}>{team.name}</span>
        <span className="flex-1" />
        <span className="font-mono text-fg-faint" style={{ fontSize: 10 }}>{team.members.length}</span>
      </div>
      {team.members.map((m) => (
        <div key={m.key} className="flex items-center gap-2 border-b border-dashed border-line last:border-b-0" style={{ padding: '7px 13px' }}>
          <span className="rounded-full flex-shrink-0" style={{ width: 5, height: 5, background: VIO.accent }} />
          <span className="font-semibold text-fg truncate" style={{ fontSize: 12.5 }}>{m.nick}</span>
          {team.captain === m.key && <Ic name="medal" size={12} color="var(--fed-gold)" />}
          {userKey && m.key === userKey && <Pill color={VIO.bright} bg={VIO.soft} border={VIO.line}>{M.team_you}</Pill>}
        </div>
      ))}
    </div>
  );
}

/** Панель выбранного матча: старт окна, чек-ин, составы, обмен ID,
    скриншот-подтверждение и результат (админ). `remaining` — секунд до конца
    15-мин окна чек-ина (тикает в родителе), null — окно не запущено. */
function MatchPanel({ match, teams, myTeamId, B, M, isAdmin, remaining, matchesOpen, onClose = () => {} }) {
  const fire = useAppStore((s) => s.fire);
  const user = useAppStore((s) => s.user);
  const { ready, toggleReady, readyAll, setResult, openMatch, resolveMatch, submitProof } = useMlbb();
  const [sc, setSc] = useState({ sa: match.sa ?? '', sb: match.sb ?? '' });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => { setSc({ sa: match.sa ?? '', sb: match.sb ?? '' }); }, [match.id, match.sa, match.sb]);

  const tA = teams.find((t) => t.id === match.a) || null;
  const tB = teams.find((t) => t.id === match.b) || null;
  const bothKnown = !!(tA && tB);
  // матч завершён исходом: победитель / тех.победа / двойное тех. / подтверждён
  const resolved = match.voided || !!match.winner || match.status === 'walkover' || match.status === 'confirmed' || match.status === 'double_ff';
  // чек-ин активен только после того, как админ открыл матч (старт окна)
  const checkinOpen = !!(bothKnown && !resolved && match.opened);
  const winnerTeam = match.winner ? teams.find((t) => t.id === match.winner) : null;

  const readyOf = (team) => (team ? ready[`${match.id}|${team.id}`] || [] : []);
  // Считаем готовыми только отметки, соответствующие ТЕКУЩЕМУ составу — так
  // раскрытие ID не зависит от устаревших ключей (ушедший игрок, смена состава)
  // и срабатывает надёжно ровно когда явилась вся основа.
  const readyMembers = (team) => (team ? readyOf(team).filter((k) => team.members.some((m) => m.key === k)) : []);
  const fullReady = (team) => !!team && readyMembers(team).length >= requiredOf(team);
  const bothReady = fullReady(tA) && fullReady(tB);

  const mySide = myTeamId === match.a ? tA : myTeamId === match.b ? tB : null;
  const oppSide = mySide ? (mySide === tA ? tB : tA) : null;
  const userKey = user ? `u${user.id ?? user.nickname ?? user.email}` : null;
  const iAmInMatch = !!(mySide && userKey && mySide.members.some((m) => m.key === userKey));
  const iAmCaptain = iAmInMatch && mySide.captain === userKey;

  const copyIds = async () => {
    const txt = oppSide.members.map((m) => `${m.nick} — ID ${m.gameId} — ${m.tg}`).join('\n');
    const ok = await copyToClipboard(txt);
    fire(ok ? B.copied : B.copy_fail, ok ? 'success' : 'error');
  };

  // Копировать один игровой ID по нажатию — удобно вбивать в игру не вручную.
  const copyId = async (id) => {
    const ok = await copyToClipboard(id);
    fire(ok ? B.id_copied : B.copy_fail, ok ? 'success' : 'error');
  };

  const doUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (fileRef.current) fileRef.current.value = '';
    if (!file || !mySide) return;
    setUploading(true);
    const err = await submitProof(match.id, mySide.id, file);
    setUploading(false);
    fire(err ? B.proof_err : B.proof_ok, err ? 'error' : 'success');
  };

  const Roster = ({ team }) => {
    if (!team) return null;
    const r = readyOf(team);
    const rc = readyMembers(team).length;   // готовы из текущего состава
    const isMyTeam = mySide && team.id === mySide.id;
    const revealIds = bothReady && (iAmInMatch || isAdmin);
    return (
      <div className="rounded-xl border border-line overflow-hidden" style={{ background: 'rgba(10,10,12,.45)', borderColor: isMyTeam ? VIO.line : undefined }}>
        <div className="flex items-center gap-2 border-b border-line" style={{ padding: '10px 13px' }}>
          <span className="font-display font-bold text-fg" style={{ fontSize: 14 }}>{team.name}</span>
          <span className="flex-1" />
          <span className="font-mono" style={{ fontSize: 10.5, color: rc >= requiredOf(team) ? 'var(--fed-ok)' : 'var(--fed-fg-faint)' }}>
            {rc}/{requiredOf(team)} {B.ready_of}
          </span>
        </div>
        {team.members.map((m) => {
          const isReady = r.includes(m.key);
          const isMe = userKey && m.key === userKey;
          // Готовность может ставить: сам игрок — свою; капитан — за любого в
          // своей команде; админ — за кого угодно в любой команде.
          const canToggle = checkinOpen && (isAdmin || (isMyTeam && (isMe || iAmCaptain)));
          return (
            <div key={m.key} className="flex items-center gap-2.5 border-b border-dashed border-line last:border-b-0" style={{ padding: '8px 13px' }}>
              <span
                className="flex-shrink-0 flex items-center justify-center rounded-full"
                style={{
                  width: 20, height: 20,
                  background: isReady ? 'rgba(67,217,106,.15)' : 'var(--fed-surface-2)',
                  border: `1px solid ${isReady ? 'rgba(67,217,106,.5)' : 'var(--fed-line-2)'}`,
                  color: 'var(--fed-ok)',
                }}
              >
                {isReady && <Ic name="check" size={11} sw={3} />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5">
                  <span className="font-semibold text-fg truncate" style={{ fontSize: 12.5 }}>{m.nick}</span>
                  {team.captain === m.key && <Ic name="medal" size={12} color="var(--fed-gold)" />}
                  {isMe && <Pill color={VIO.bright} bg={VIO.soft} border={VIO.line}>{M.team_you}</Pill>}
                </span>
                {revealIds && !isMyTeam && (
                  <button
                    onClick={() => copyId(m.gameId)}
                    title={B.id_copy_hint}
                    className="inline-flex items-center gap-1.5 font-mono cursor-pointer bg-transparent border-none p-0 mt-1 hover:opacity-80"
                    style={{ fontSize: 10.5, color: VIO.bright }}
                  >
                    <span className="truncate">ID {m.gameId} · {m.tg}</span>
                    <Ic name="doc" size={11} />
                  </button>
                )}
              </span>
              {canToggle && (
                <button
                  onClick={() => toggleReady(match.id, team.id, m.key)}
                  className="flex-shrink-0 h-8 px-3 rounded-lg cursor-pointer font-display font-semibold uppercase"
                  style={{
                    fontSize: 10.5, letterSpacing: '.04em',
                    border: isReady ? '1px solid var(--fed-line-2)' : 'none',
                    background: isReady ? 'transparent' : 'linear-gradient(120deg,#3BA55D,#227940)',
                    color: isReady ? 'var(--fed-fg-muted)' : '#fff',
                  }}
                >
                  {isReady ? B.im_unready : B.im_ready}
                </button>
              )}
            </div>
          );
        })}
        {checkinOpen && ((isMyTeam && iAmInMatch) || isAdmin) && rc < requiredOf(team) && (
          <div style={{ padding: '9px 13px' }}>
            <button onClick={() => readyAll(match.id, team.id, team.members.map((m) => m.key))} className="h-8 px-3 rounded-lg cursor-pointer border border-dashed border-line bg-transparent text-fg-muted hover:text-fg" style={{ fontSize: 11 }}>
              {B.demo_ready_all}
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <Rv className="rounded-2xl border overflow-hidden" style={{ borderColor: VIO.line, background: 'linear-gradient(160deg,var(--fed-surface-2),var(--fed-surface))', boxShadow: `0 0 42px -18px ${VIO.glow}` }}>
      <div className="flex items-center gap-3 flex-wrap border-b border-line" style={{ padding: '14px 18px' }}>
        <Ic name="zap" size={17} color={VIO.bright} />
        <span className="font-display font-bold uppercase text-fg" style={{ fontSize: 15, letterSpacing: '.04em' }}>{B.checkin_t}</span>
        <span className="flex-1" />
        {match.voided && <Pill color="var(--fed-accent-bright)" bg="rgba(225,18,23,.1)" border="var(--fed-line-accent)">{B.st_dff}</Pill>}
        {match.status === 'walkover' && winnerTeam && <Pill color="var(--fed-gold)" bg="rgba(230,183,60,.1)" border="rgba(230,183,60,.35)">{B.st_walkover}: {winnerTeam.name}</Pill>}
        {match.status === 'confirmed' && winnerTeam && <Pill color="var(--fed-gold)" bg="rgba(230,183,60,.1)" border="rgba(230,183,60,.35)">{B.winner}: {winnerTeam.name}</Pill>}
        {match.status === 'pending' && !resolved && <Pill color={VIO.bright} bg={VIO.soft} border={VIO.line} dot>{B.st_pending}</Pill>}
        {!resolved && match.opened && bothReady && <Pill color="var(--fed-ok)" bg="rgba(67,217,106,.12)" border="rgba(67,217,106,.4)" dot>{B.st_ready}</Pill>}
        <button onClick={onClose} title={B.close} aria-label={B.close} className="flex-shrink-0 inline-flex items-center justify-center rounded-lg cursor-pointer bg-transparent text-fg-muted hover:text-fg" style={{ width: 34, height: 34, border: '1px solid var(--fed-line-2)' }}>
          <Ic name="x" size={16} />
        </button>
      </div>

      <div style={{ padding: 18 }}>
        {/* составы — видны сразу по клику, не дожидаясь старта чек-ина.
            Когда чек-ин открыт, ниже показывается полный ростер с готовностью
            и раскрытием ID, поэтому здесь дублировать не нужно. */}
        {!checkinOpen && (tA || tB) && (
          <div className="grid grid-cols-2 gap-3.5 max-[700px]:grid-cols-1" style={{ marginBottom: 14 }}>
            <RosterMini team={tA} userKey={userKey} M={M} />
            <RosterMini team={tB} userKey={userKey} M={M} />
          </div>
        )}
        {/* обе команды ещё не определены */}
        {!bothKnown && !resolved && (
          <div className="flex items-center gap-3 text-fg-muted" style={{ fontSize: 13.5 }}>
            <span className="flex-shrink-0 flex items-center justify-center rounded-[9px]" style={{ width: 34, height: 34, background: 'var(--fed-surface-2)', border: '1px solid var(--fed-line-2)' }}>
              <Ic name="lock" size={16} color="var(--fed-fg-faint)" />
            </span>
            {B.checkin_locked}
          </div>
        )}

        {/* итог матча: тех.победа / двойное тех. / подтверждён админом */}
        {resolved && (
          <div className="rounded-xl" style={{ padding: 16, border: `1px solid ${match.voided ? 'var(--fed-line-accent)' : 'rgba(230,183,60,.35)'}`, background: match.voided ? 'rgba(225,18,23,.07)' : 'rgba(230,183,60,.08)' }}>
            <div className="flex items-center gap-2.5">
              <Ic name={match.voided ? 'ban' : 'trophy'} size={20} color={match.voided ? 'var(--fed-accent-bright)' : 'var(--fed-gold)'} />
              <span className="font-display font-bold text-fg" style={{ fontSize: 15.5 }}>
                {match.voided ? B.out_dff_t : match.status === 'walkover' ? B.out_walkover : B.out_confirmed}{winnerTeam && !match.voided ? `: ${winnerTeam.name}` : ''}
              </span>
            </div>
            {(match.voided || match.status === 'walkover') && (
              <p className="text-fg-muted" style={{ fontSize: 12.5, marginTop: 6, lineHeight: 1.5 }}>{match.voided ? B.out_dff_d : B.out_walkover_d}</p>
            )}
          </div>
        )}

        {/* обе команды известны, но матч ещё не открыт — старт 15-мин окна */}
        {bothKnown && !resolved && !match.opened && (
          <div className="flex items-start gap-3 rounded-xl" style={{ padding: 16, border: `1px solid ${VIO.line}`, background: VIO.soft }}>
            <span className="flex-shrink-0 flex items-center justify-center rounded-[9px]" style={{ width: 36, height: 36, background: 'rgba(8,8,10,.4)', border: `1px solid ${VIO.line}` }}>
              <Ic name="lock" size={17} color={VIO.bright} />
            </span>
            <div className="min-w-0">
              <p className="text-fg-muted" style={{ fontSize: 12.5, lineHeight: 1.5 }}>{!isAdmin ? B.await_start_user : matchesOpen ? B.await_start_admin : B.need_matches_open}</p>
              {isAdmin && matchesOpen && (
                <button onClick={() => openMatch(match.id, match.a, match.b)} className="h-9 px-4 rounded-[9px] cursor-pointer border-none font-display font-semibold uppercase text-white mt-2.5" style={{ fontSize: 12, letterSpacing: '.03em', background: `linear-gradient(120deg,${VIO.accent},${VIO.deep})` }}>
                  {B.open_win}
                </button>
              )}
            </div>
          </div>
        )}

        {/* активный чек-ин: окно открыто */}
        {checkinOpen && (
          <>
            {typeof remaining === 'number' && (remaining > 0 || !bothReady) && (
              <div className="flex items-center gap-3 rounded-xl" style={{ padding: '10px 14px', marginBottom: 14, border: `1px solid ${VIO.line}`, background: VIO.soft }}>
                <Ic name="zap" size={18} color={remaining > 0 ? VIO.bright : 'var(--fed-fg-faint)'} />
                {remaining > 0 ? (
                  <>
                    <span className="font-display font-bold" style={{ fontSize: 21, color: remaining <= 60 ? 'var(--fed-accent-bright)' : VIO.bright, fontVariantNumeric: 'tabular-nums' }}>{fmtClock(remaining)}</span>
                    <span className="text-fg-muted" style={{ fontSize: 12.5 }}>{B.win_left}</span>
                  </>
                ) : (
                  <span className="text-fg-muted" style={{ fontSize: 13 }}>{B.win_over}</span>
                )}
              </div>
            )}
            <p className="text-fg-muted" style={{ fontSize: 12.5, lineHeight: 1.55, marginBottom: 8 }}>{B.checkin_d}</p>
            <p className="text-fg-faint" style={{ fontSize: 11.5, lineHeight: 1.5, marginBottom: 14 }}>{B.win_hint}</p>
            {!iAmInMatch && !isAdmin && <p className="text-fg-faint" style={{ fontSize: 12, marginBottom: 14 }}>{B.watch_only}</p>}

            <div className="grid grid-cols-2 gap-3.5 max-[700px]:grid-cols-1">
              <Roster team={tA} />
              <Roster team={tB} />
            </div>

            {/* соперники готовятся */}
            {mySide && oppSide && fullReady(mySide) && !fullReady(oppSide) && (
              <div className="flex items-center gap-2.5 text-fg-muted" style={{ fontSize: 13, marginTop: 14 }}>
                <span className="fed-dot rounded-full" style={{ width: 8, height: 8, background: VIO.accent, boxShadow: `0 0 10px ${VIO.glow}` }} />
                {B.opp_preparing}
              </div>
            )}

            {/* обмен ID */}
            {bothReady && iAmInMatch && oppSide && (
              <div className="rounded-xl mt-4" style={{ padding: 16, background: VIO.soft, border: `1px solid ${VIO.line}` }}>
                <div className="flex items-center gap-2.5 flex-wrap">
                  <Ic name="gamepad" size={18} color={VIO.bright} />
                  <span className="font-display font-bold text-fg" style={{ fontSize: 14.5 }}>{B.ids_t}</span>
                  <span className="text-fg-muted" style={{ fontSize: 12 }}>{B.ids_d}</span>
                  <span className="flex-1" />
                  <button onClick={copyIds} className="h-9 px-3.5 rounded-[9px] cursor-pointer border-none font-display font-semibold uppercase text-white" style={{ fontSize: 11.5, background: `linear-gradient(120deg,${VIO.accent},${VIO.deep})` }}>
                    {B.copy_ids}
                  </button>
                </div>
                <div className="flex items-center gap-1.5 text-fg-faint" style={{ fontSize: 11, marginTop: 8 }}>
                  <Ic name="doc" size={12} color={VIO.bright} /> {B.id_copy_hint}
                </div>
                <div className="grid gap-1.5 mt-2.5" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))' }}>
                  {oppSide.members.map((m) => (
                    <button
                      key={m.key}
                      onClick={() => copyId(m.gameId)}
                      title={B.id_copy_hint}
                      className="group flex items-center gap-2 rounded-lg cursor-pointer border-none text-left w-full hover:brightness-125"
                      style={{ padding: '7px 10px', background: 'rgba(6,6,8,.4)' }}
                    >
                      <span className="font-semibold text-fg truncate" style={{ fontSize: 12 }}>{m.nick}</span>
                      <span className="font-mono ml-auto" style={{ fontSize: 11, color: VIO.bright }}>{m.gameId}</span>
                      <Ic name="doc" size={12} color="var(--fed-fg-faint)" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* скриншот-подтверждение результата — участник матча */}
        {(match.opened || resolved) && iAmInMatch && !isAdmin && (
          <div className="rounded-xl mt-4" style={{ padding: 14, border: '1px solid var(--fed-line-2)', background: 'rgba(8,8,10,.4)' }}>
            <div className="flex items-center gap-2 flex-wrap">
              <Ic name="doc" size={16} color={VIO.bright} />
              <span className="font-display font-bold text-fg" style={{ fontSize: 13.5 }}>{B.proof_t}</span>
              <span className="flex-1" />
              {match.proof && <a href={`${FILE_BASE}/${match.proof}`} target="_blank" rel="noreferrer" className="text-accent-bright" style={{ fontSize: 12.5 }}>{B.proof_view}</a>}
            </div>
            <p className="text-fg-muted" style={{ fontSize: 12, marginTop: 6, lineHeight: 1.5 }}>{B.proof_hint}</p>
            <input ref={fileRef} type="file" accept="image/*" onChange={doUpload} style={{ display: 'none' }} />
            <button onClick={() => fileRef.current && fileRef.current.click()} disabled={uploading} className="h-9 px-4 rounded-[9px] cursor-pointer border-none font-display font-semibold uppercase text-white mt-2.5" style={{ fontSize: 12, background: `linear-gradient(120deg,${VIO.accent},${VIO.deep})`, opacity: uploading ? 0.6 : 1 }}>
              {uploading ? B.proof_uploading : B.proof_upload}
            </button>
            {match.status === 'pending' && <div className="text-fg-faint mt-2" style={{ fontSize: 11.5 }}>{B.proof_pending}</div>}
          </div>
        )}

        {/* панель админа: старт/перезапуск окна, результат, счёт, скриншот */}
        {isAdmin && bothKnown && (
          <div className="mt-4 border-t border-dashed border-line" style={{ paddingTop: 14 }}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="fed-label">{B.res_t}</span>
              {match.proof && (
                <a href={`${FILE_BASE}/${match.proof}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-accent-bright" style={{ fontSize: 12.5 }}>
                  <Ic name="doc" size={13} /> {B.proof_view}
                </a>
              )}
            </div>
            <p className="text-fg-faint" style={{ fontSize: 11.5, marginTop: 4, lineHeight: 1.45 }}>{B.res_hint}</p>

            <div className="flex items-center gap-2 flex-wrap" style={{ marginTop: 10 }}>
              <button onClick={() => resolveMatch(match.id, tA.id, 'confirmed')} className="h-9 px-3.5 rounded-[9px] cursor-pointer font-display font-semibold uppercase" style={{ fontSize: 11.5, border: match.winner === tA.id ? 'none' : '1px solid var(--fed-line-2)', background: match.winner === tA.id ? 'linear-gradient(120deg,#C79A2B,#8A6A18)' : 'var(--fed-surface-2)', color: match.winner === tA.id ? '#fff' : 'var(--fed-fg-soft)' }}>
                {B.res_win_a} · {tA.tag}
              </button>
              <button onClick={() => resolveMatch(match.id, tB.id, 'confirmed')} className="h-9 px-3.5 rounded-[9px] cursor-pointer font-display font-semibold uppercase" style={{ fontSize: 11.5, border: match.winner === tB.id ? 'none' : '1px solid var(--fed-line-2)', background: match.winner === tB.id ? 'linear-gradient(120deg,#C79A2B,#8A6A18)' : 'var(--fed-surface-2)', color: match.winner === tB.id ? '#fff' : 'var(--fed-fg-soft)' }}>
                {B.res_win_a} · {tB.tag}
              </button>
              <button onClick={() => resolveMatch(match.id, null, 'double_ff')} className="h-9 px-3.5 rounded-[9px] cursor-pointer font-display font-semibold uppercase" style={{ fontSize: 11.5, border: match.voided ? 'none' : '1px solid var(--fed-line-accent)', background: match.voided ? 'rgba(225,18,23,.9)' : 'rgba(225,18,23,.08)', color: match.voided ? '#fff' : 'var(--fed-accent-bright)' }}>
                {B.res_dff}
              </button>
              {resolved && (
                <button onClick={() => resolveMatch(match.id, null, '')} className="h-9 px-3.5 rounded-[9px] cursor-pointer border border-line bg-transparent text-fg-muted hover:text-fg" style={{ fontSize: 11.5 }}>
                  {B.res_reset}
                </button>
              )}
              {!resolved && match.opened && matchesOpen && (
                <button onClick={() => openMatch(match.id, match.a, match.b)} className="h-9 px-3.5 rounded-[9px] cursor-pointer border border-line bg-transparent text-fg-muted hover:text-fg" style={{ fontSize: 11.5 }}>
                  {B.reopen_win}
                </button>
              )}
            </div>

            {/* счёт — для истории */}
            <div className="flex items-end gap-2.5 flex-wrap" style={{ marginTop: 12 }}>
              <span className="fed-label">{B.score_t}</span>
              <input className="fed-input text-center font-display" style={{ width: 58, height: 38 }} inputMode="numeric" value={sc.sa} onChange={(e) => setSc({ ...sc, sa: e.target.value.replace(/\D/g, '') })} />
              <span className="text-fg-faint">:</span>
              <input className="fed-input text-center font-display" style={{ width: 58, height: 38 }} inputMode="numeric" value={sc.sb} onChange={(e) => setSc({ ...sc, sb: e.target.value.replace(/\D/g, '') })} />
              <button onClick={() => setResult(match.id, sc.sa, sc.sb)} className="fed-btn-ghost h-[38px] px-4 rounded-[9px]" style={{ fontSize: 12.5 }}>
                {B.score_save}
              </button>
            </div>
          </div>
        )}
      </div>
    </Rv>
  );
}

export default function MlbbBracket({ M, userKey, onGoReg, matchesOpen = true, tourId = MLBB_ACTIVE_TOUR_ID }) {
  const { lang } = useFedStore();
  const user = useAppStore((s) => s.user);
  const B = BD[lang];
  const isAdmin = !!user && (user.user_type === 'admin' || user.role === 'admin');
  // матчи закрыты (идёт регистрация): сетка только для просмотра, чек-ина нет.
  // Админ видит контролы всегда — чтобы можно было проставлять результаты.
  const live = matchesOpen || isAdmin;

  const mlbb = useMlbb();
  const regIds = regTeamIdsOf(mlbb, tourId);
  const rounds = useMemo(() => buildRounds(regIds, mlbb.results), [regIds.join(','), mlbb.results]); // eslint-disable-line react-hooks/exhaustive-deps

  const myTeam = userKey ? mlbb.teams.find((t) => regIds.includes(t.id) && t.members.some((m) => m.key === userKey)) : null;
  const myTeamId = myTeam ? myTeam.id : null;

  const [selectedId, setSelectedId] = useState(null);
  const matchRefs = useRef({});
  const gridRef = useRef(null);
  const [links, setLinks] = useState([]);
  const panelRef = useRef(null);        // панель выбранного матча (внизу)
  const scrollToPanel = useRef(false);  // проскроллить к панели после клика по матчу

  // Соединительные линии между раундами: измеряем позиции карточек матчей и
  // рисуем SVG-связи «матч → матч следующего раунда» (r{ri}m{mi} → r{ri+1}m{mi/2}).
  // Координаты — в системе grid-контейнера (он же — прокручиваемый контент),
  // поэтому линии не «съезжают» при горизонтальном скролле.
  useLayoutEffect(() => {
    const measure = () => {
      const grid = gridRef.current;
      if (!grid) return;
      const gb = grid.getBoundingClientRect();
      const out = [];
      rounds.forEach((matches, ri) => {
        if (ri >= rounds.length - 1) return;
        matches.forEach((m) => {
          if (m.dead) return; // «мёртвая» ветка без команд — связывать нечего
          const srcEl = matchRefs.current[m.id];
          const dstEl = matchRefs.current[`r${ri + 1}m${Math.floor(m.mi / 2)}`];
          if (!srcEl || !dstEl) return;
          const s = srcEl.getBoundingClientRect();
          const d = dstEl.getBoundingClientRect();
          const x1 = s.right - gb.left;
          const y1 = s.top - gb.top + s.height / 2;
          const x2 = d.left - gb.left;
          const y2 = d.top - gb.top + d.height / 2;
          const midX = x1 + Math.max(14, (x2 - x1) / 2); // колено на середине зазора
          out.push({ key: m.id, d: `M ${x1} ${y1} H ${midX} V ${y2} H ${x2}`, active: !!m.winner });
        });
      });
      setLinks(out);
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro && gridRef.current) ro.observe(gridRef.current);
    window.addEventListener('resize', measure);
    return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', measure); };
  }, [rounds, live]);

  // тик раз в секунду — для живого обратного отсчёта окна чек-ина
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => (x + 1) % 1e9), 1000);
    return () => clearInterval(t);
  }, []);
  // момент последней синхронизации с сервером: от него локально отсчитываем
  // секунды между поллингами (сервер присылает remaining на момент ответа)
  const fetchedAt = useRef(Date.now());
  useEffect(() => { fetchedAt.current = Date.now(); }, [mlbb.results]);
  const remainingOf = (m) => {
    if (!m || m.remaining == null || !m.opened || m.status || m.winner || m.bye || m.voided) return null;
    return Math.max(0, m.remaining - Math.floor((Date.now() - fetchedAt.current) / 1000));
  };

  // путь моей команды: сыгранные/текущие матчи + возможные будущие
  const { actualPath, futurePath, myCurrent } = useMemo(() => {
    const actual = new Set();
    const future = new Set();
    let current = null;
    if (myTeamId) {
      let eliminated = false;
      let last = null;
      rounds.forEach((matches) =>
        matches.forEach((m) => {
          if (m.a === myTeamId || m.b === myTeamId) {
            actual.add(m.id);
            last = m;
            if (m.winner && m.winner !== myTeamId) eliminated = true;
          }
        })
      );
      if (last && !last.winner) current = last;
      if (last && !eliminated && !(last.ri === rounds.length - 1 && last.winner === myTeamId)) {
        let ri = last.ri;
        let mi = last.mi;
        while (ri < rounds.length - 1) {
          ri += 1;
          mi = Math.floor(mi / 2);
          const id = `r${ri}m${mi}`;
          if (!actual.has(id)) future.add(id);
        }
      }
    }
    return { actualPath: actual, futurePath: future, myCurrent: current };
  }, [rounds, myTeamId]);

  const teamOf = (id) => mlbb.teams.find((t) => t.id === id) || null;
  const readyCount = (m, teamId) => (teamId ? (mlbb.ready[`${m.id}|${teamId}`] || []).length : 0);

  const statusOf = (m) => {
    if (m.dead) return { label: '—', color: 'var(--fed-fg-faint)' };
    if (m.bye) return { label: B.st_bye, color: 'var(--fed-fg-faint)' };
    if (m.voided) return { label: B.st_dff, color: 'var(--fed-accent-bright)' };
    if (m.status === 'walkover') return { label: B.st_walkover, color: 'var(--fed-gold)' };
    if (m.winner) return { label: B.st_done, color: 'var(--fed-fg-muted)' };
    // регистрация: пара известна, но матчи ещё не открыты — нейтральный статус
    if (!live && m.a && m.b) return { label: B.st_seed, color: 'var(--fed-fg-muted)' };
    if (m.a && m.b) {
      if (!m.opened) return { label: B.st_await_start, color: 'var(--fed-fg-faint)' };
      if (m.status === 'pending') return { label: B.st_pending, color: VIO.bright };
      const both = readyCount(m, m.a) >= requiredOf(teamOf(m.a)) && readyCount(m, m.b) >= requiredOf(teamOf(m.b));
      const rem = remainingOf(m);
      // окно истекло: обе явились → идёт матч; иначе подводим итог по явке
      if (rem === 0) return both ? { label: B.st_playing, color: 'var(--fed-ok)' } : { label: B.st_resolving, color: 'var(--fed-fg-muted)' };
      const base = both ? B.st_ready : B.st_checkin;
      return { label: rem != null ? `${base} ${fmtClock(rem)}` : base, color: both ? 'var(--fed-ok)' : VIO.bright };
    }
    return { label: B.st_wait, color: 'var(--fed-fg-faint)' };
  };

  const scrollToMy = () => {
    const target = myCurrent || rounds.flat().find((m) => actualPath.has(m.id));
    if (!target) return;
    setSelectedId(target.id);
    const el = matchRefs.current[target.id];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  };

  // Автоскролл к панели матча после клика по нему — «сдвигает страницу вниз»
  // к составам/чек-ину. Только по клику на матч (флаг scrollToPanel), не по
  // кнопке «Моя команда» (та скроллит к карточке в сетке).
  useEffect(() => {
    if (selectedId && scrollToPanel.current && panelRef.current) {
      panelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    scrollToPanel.current = false;
  }, [selectedId]);

  if (!regIds.length) {
    return (
      <Rv className="rounded-2xl bg-surface border border-line text-center" style={{ padding: '44px 24px' }}>
        <div className="font-display font-bold uppercase text-fg" style={{ fontSize: 22 }}>{B.empty_t}</div>
        <button onClick={onGoReg} className="h-11 px-6 rounded-[11px] cursor-pointer border-none font-display font-semibold uppercase text-white" style={{ marginTop: 16, fontSize: 14, background: `linear-gradient(120deg,${VIO.accent},${VIO.deep})` }}>
          {B.empty_cta} →
        </button>
      </Rv>
    );
  }

  const titles = ROUND_TITLES[lang];
  const selected = selectedId ? rounds.flat().find((m) => m.id === selectedId) : null;

  return (
    <div className="flex flex-col gap-5">
      {/* баннер: матчи ещё закрыты (идёт регистрация) */}
      {!matchesOpen && (
        <Rv className="flex items-start gap-3 rounded-2xl border" style={{ padding: '15px 17px', borderColor: VIO.line, background: VIO.soft }}>
          <span className="flex-shrink-0 flex items-center justify-center rounded-xl" style={{ width: 40, height: 40, background: 'rgba(8,8,10,.4)', border: `1px solid ${VIO.line}` }}>
            <Ic name="lock" size={18} color={VIO.bright} />
          </span>
          <div>
            <div className="font-display font-bold text-fg" style={{ fontSize: 15.5 }}>{B.closed_t}</div>
            <p className="text-fg-muted" style={{ fontSize: 12.5, lineHeight: 1.5, marginTop: 3 }}>{B.closed_d}</p>
          </div>
        </Rv>
      )}

      {/* панель управления: моя команда + легенда */}
      <Rv className="flex items-center gap-3.5 flex-wrap">
        {myTeamId && (
          <button
            onClick={scrollToMy}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-[10px] cursor-pointer border-none font-display font-semibold uppercase text-white"
            style={{ fontSize: 12.5, letterSpacing: '.04em', background: `linear-gradient(120deg,${VIO.accent},${VIO.deep})`, boxShadow: `0 8px 20px -8px ${VIO.glow}` }}
          >
            <Ic name="pin" size={14} /> {B.my_team} · {myTeam.name}
          </button>
        )}
        {myTeamId && (
          <>
            <span className="inline-flex items-center gap-2 text-fg-muted" style={{ fontSize: 12.5 }}>
              <i className="rounded-[3px]" style={{ width: 14, height: 3, background: VIO.accent }} /> {B.legend_path}
            </span>
            <span className="inline-flex items-center gap-2 text-fg-muted" style={{ fontSize: 12.5 }}>
              <i style={{ width: 14, borderTop: `2px dashed ${VIO.line}` }} /> {B.legend_future}
            </span>
          </>
        )}
        {!live ? (
          <span className="text-fg-faint" style={{ fontSize: 12.5 }}>{B.preview_hint}</span>
        ) : !selected ? (
          <span className="text-fg-faint" style={{ fontSize: 12.5 }}>{B.sel_hint}</span>
        ) : null}
        <span className="flex-1" />
        {isAdmin && (
          <button onClick={() => (window.confirm('Сбросить турнир? Команды, заявки, счёт и чек-ин будут удалены.') ? mlbb.resetMlbb() : null)} className="h-9 px-3.5 rounded-[9px] cursor-pointer border border-line bg-transparent text-fg-faint hover:text-fg" style={{ fontSize: 12 }}>
            {B.reset_demo}
          </button>
        )}
      </Rv>

      {/* сетка */}
      <div className="overflow-x-auto" style={{ paddingBottom: 10 }}>
        <div ref={gridRef} className="relative flex gap-6 items-stretch" style={{ width: 'max-content', minWidth: '100%' }}>
          {/* соединительные линии между раундами (рисуются под карточками) */}
          <svg className="absolute inset-0 pointer-events-none" style={{ width: '100%', height: '100%', zIndex: 0, overflow: 'visible' }}>
            {links.map((l) => (
              <path
                key={l.key}
                d={l.d}
                fill="none"
                strokeWidth="2"
                strokeLinejoin="round"
                strokeLinecap="round"
                stroke={l.active ? VIO.accent : 'var(--fed-line-2)'}
                style={l.active ? { filter: `drop-shadow(0 0 4px ${VIO.glow})` } : undefined}
              />
            ))}
          </svg>
          {rounds.map((matches, ri) => (
            <div key={ri} className="relative flex flex-col gap-3.5 flex-1" style={{ minWidth: 250, zIndex: 1 }}>
              <div className="text-center font-display font-bold uppercase text-fg border-b border-line" style={{ fontSize: 14.5, letterSpacing: '.06em', paddingBottom: 6 }}>
                {titles[matches.length] || `Раунд ${ri + 1}`}
              </div>
              <div className="flex flex-col gap-3.5 justify-around flex-1">
                {matches.map((m) => {
                  const mine = actualPath.has(m.id);
                  const future = futurePath.has(m.id);
                  const st = statusOf(m);
                  const isSel = selectedId === m.id;
                  const tA = teamOf(m.a);
                  const tB = teamOf(m.b);
                  const showCheckin = live && m.opened && !!(m.a && m.b && !m.winner && !m.voided);
                  const clickable = live && !m.dead; // BYE тоже кликабелен — посмотреть состав
                  return (
                    <div
                      key={m.id}
                      ref={(el) => { matchRefs.current[m.id] = el; }}
                      onClick={clickable ? () => { scrollToPanel.current = !isSel; setSelectedId(isSel ? null : m.id); } : undefined}
                      className={`overflow-hidden rounded-xl shadow-card ${clickable ? 'cursor-pointer fed-hover-lift' : ''}`}
                      style={{
                        '--lift': '-2px',
                        background: 'linear-gradient(160deg,var(--fed-surface-2),var(--fed-surface))',
                        border: mine ? `1.5px solid ${VIO.accent}` : future ? `1.5px dashed ${VIO.line}` : isSel ? `1px solid ${VIO.line}` : '1px solid var(--fed-line-2)',
                        boxShadow: mine || isSel ? `0 0 26px -12px ${VIO.glow}` : undefined,
                      }}
                    >
                      <div className="flex items-center justify-between border-b border-line" style={{ padding: '5px 12px', background: 'rgba(6,6,8,.35)' }}>
                        <span className="font-mono uppercase text-fg-faint" style={{ fontSize: 8.5, letterSpacing: '.12em' }}>
                          {m.bye ? B.bye : `Матч ${m.mi + 1}`}
                        </span>
                        <span className="font-mono uppercase" style={{ fontSize: 8.5, letterSpacing: '.1em', color: st.color }}>● {st.label}</span>
                      </div>
                      <SideRow team={tA} score={m.sa} win={m.winner && m.winner === m.a} isMine={myTeamId && m.a === myTeamId} ready={readyCount(m, m.a)} req={requiredOf(tA)} showCheckin={showCheckin} B={B} />
                      <div className="border-t border-dashed border-line">
                        <SideRow team={tB} score={m.sb} win={m.winner && m.winner === m.b} isMine={myTeamId && m.b === myTeamId} ready={readyCount(m, m.b)} req={requiredOf(tB)} showCheckin={showCheckin} B={B} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* панель выбранного матча — только когда матчи открыты или админ */}
      {live && selected && !selected.dead && (selected.bye ? (
        // BYE: соперника нет — показываем состав прошедшей команды
        <div ref={panelRef} style={{ scrollMarginTop: 76 }}>
          <Rv className="rounded-2xl border overflow-hidden" style={{ borderColor: VIO.line, background: 'linear-gradient(160deg,var(--fed-surface-2),var(--fed-surface))', boxShadow: `0 0 42px -18px ${VIO.glow}` }}>
            <div className="flex items-center gap-3 flex-wrap border-b border-line" style={{ padding: '14px 18px' }}>
              <Ic name="zap" size={17} color={VIO.bright} />
              <span className="font-display font-bold uppercase text-fg" style={{ fontSize: 15, letterSpacing: '.04em' }}>{B.bye_t}</span>
              <span className="flex-1" />
              <button onClick={() => setSelectedId(null)} title={B.close} aria-label={B.close} className="flex-shrink-0 inline-flex items-center justify-center rounded-lg cursor-pointer bg-transparent text-fg-muted hover:text-fg" style={{ width: 34, height: 34, border: '1px solid var(--fed-line-2)' }}>
                <Ic name="x" size={16} />
              </button>
            </div>
            <div style={{ padding: 18 }}>
              <p className="text-fg-muted" style={{ fontSize: 12.5, lineHeight: 1.5, marginBottom: 14 }}>{B.bye_d}</p>
              <div className="max-w-[380px]">
                <RosterMini team={teamOf(selected.winner || selected.a || selected.b)} userKey={userKey} M={M} />
              </div>
            </div>
          </Rv>
        </div>
      ) : (
        <div ref={panelRef} style={{ scrollMarginTop: 76 }}>
          <MatchPanel key={selected.id} match={selected} teams={mlbb.teams} myTeamId={myTeamId} B={B} M={M} isAdmin={isAdmin} remaining={remainingOf(selected)} matchesOpen={matchesOpen} onClose={() => setSelectedId(null)} />
        </div>
      ))}
    </div>
  );
}
